import { Box, CircularProgress, Typography } from '@mui/material';
import { FC, useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../aws/AuthProvider';
import { DataContext } from '../../../aws/DataProvider';
import {
  getOrderFollowupRequest,
  getOrderRequest,
  handleComment,
  handleCommentFollowup,
  handleFollowup,
  handleRequest,
  generateRezept,
  listOrderRequests,
} from '../../../aws/RequestsApi';
import { useStyles } from '../../../theme/styles.helpers';
import { Utils, Orders } from '@hellohair/types';
import { BefundungModalStyles } from './BefundungModal.Styles';
import { Survey } from '../../survey/Survey';
import { RequestModalActions } from '../request-modal/RequestModalActions';
import { PatientCard } from '../../patient-card/PatientCard';
import { ListCard } from '../../list-card/ListCard';

const formatDate = (timestamp: number): string => new Date(timestamp).toLocaleDateString('de-DE');

interface BefundungModalProps {
  onModalClose: () => void;
  requestId: string | undefined;
  isFollowup: boolean;
}

// FIXME: Once needed
// const EXCLUDED_QANDA_TYPES = ['UploadImages', 'Finish', 'Welcome'];

export const BefundungModal: FC<BefundungModalProps> = ({ onModalClose, requestId, isFollowup }) => {
  const { apiClient } = useContext(AuthContext);
  const styles = useStyles(BefundungModalStyles, {});
  const {
    ordersState: [requests, setRequests],
  } = useContext(DataContext);

  const [orderDetails, setOrderDetails] =
    useState<Utils.DBItem<Orders.OrderRequestDetails | Orders.OrderFollowupRequestDetails>>();
  const [orderHistory, setOrderHistory] = useState<Utils.DBItem<Orders.OrderRequest>[]>([]);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (requestId) {
      setLoading(true);
      fetchData(requestId);
    }
  }, [requestId]);

  const fetchData = async (reqId: string) => {
    try {
      const details = await (isFollowup
        ? getOrderFollowupRequest(reqId, apiClient, 'network-only')
        : getOrderRequest(reqId, apiClient, 'network-only'));
      setOrderDetails(details);
      setComment(details.comment ?? '');

      const history = await listOrderRequests({
        apiClient,
        fetchPolicy: 'network-only',
        email: details.customer.email,
      });
      setOrderHistory(history ?? []);
    } catch (error) {
      console.error('[BefundungModal] fetchData error:', error);
    } finally {
      setLoading(false);
    }
  };

  const onDeny = async () => {
    if (!orderDetails) return;
    try {
      setLoading(true);
      if (isFollowup) await handleFollowup(orderDetails.requestId, 'DENY', apiClient);
      else await handleRequest(orderDetails.requestId, 'DENY', apiClient);

      setRequests(
        requests.map((req) =>
          req.requestId === orderDetails.requestId ? { ...req, orderState: Orders.OrderRequestState.DENIED } : req
        )
      );
      await fetchData(orderDetails.requestId);
    } catch (error) {
      console.error('[BefundungModal] onDeny error:', error);
    } finally {
      setLoading(false);
    }
  };

  const onAccept = async () => {
    if (!orderDetails || !('product' in orderDetails)) return;
    try {
      setLoading(true);
      const response = await generateRezept(
        orderDetails.product.productId,
        orderDetails.requestId,
        orderDetails.customer.customerId,
        false,
        isFollowup,
        apiClient
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const doc = response?.members?.find((m: any) => m.isSigner);
      if (doc?.id) {
        window.open(`https://sign.sproof.com/#/editor/${doc.id}`, '_blank')?.focus();
      }
      setRequests(
        requests.map((req) =>
          req.requestId === orderDetails.requestId ? { ...req, orderState: Orders.OrderRequestState.ALLOWED } : req
        )
      );
      await fetchData(orderDetails.requestId);
      onModalClose();
    } catch (error) {
      console.error('[BefundungModal] onAccept error:', error);
    } finally {
      setLoading(false);
    }
  };

  const onSaveComment = () => {
    if (!orderDetails) return;
    if (isFollowup) handleCommentFollowup(orderDetails.requestId, comment, apiClient);
    else handleComment(orderDetails.requestId, comment, apiClient);
  };

  // FIXME: Once needed
  // const filteredQanda = orderDetails?.qanda?.filter((q) => !EXCLUDED_QANDA_TYPES.includes(q.type)) ?? [];

  // Past befundungen for this patient (isBefundung flag - populated once backend support is in place)
  const previousBefundungen = orderHistory.filter(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (req) => (req as any).isBefundung === true && req.requestId !== requestId
  );

  const orderHistoryWithoutCurrent = orderHistory.filter((req) => req.requestId !== requestId);

  return (
    <Box sx={styles.root} style={{ pointerEvents: loading ? 'none' : 'all' }}>
      {loading ? (
        <CircularProgress size={32} sx={styles.spinner} thickness={5} />
      ) : (
        <>
          {/* Header */}
          <Box sx={styles.header}>
            <Box sx={styles.typeBadge}>🧪 Befundung</Box>
            {orderDetails && <Typography sx={styles.requestId}>#{orderDetails.requestId}</Typography>}
            <Box sx={styles.closeButton} onClick={onModalClose}>
              ×
            </Box>
          </Box>

          {/* Body */}
          <Box sx={styles.body}>
            {/* Left Column */}
            <Box sx={styles.leftColumn}>
              {/* Section 1: Angefragtes Produkt - placeholder */}
              <Box sx={styles.card}>
                <Typography sx={styles.cardLabel}>Angefragtes Produkt</Typography>
              </Box>

              {/* Section 2: Dosierung & Rezept - placeholder */}
              <Box sx={styles.card}>
                <Typography sx={styles.cardLabel}>Dosierung &amp; Rezept</Typography>
              </Box>

              <Survey qanda={orderDetails?.qanda ?? []} assets={orderDetails?.assets ?? []} />

              <RequestModalActions
                comment={comment}
                onCommentChange={setComment}
                onCommentBlur={onSaveComment}
                onDeny={onDeny}
                onAccept={onAccept}
                orderState={orderDetails?.orderState}
              />
            </Box>

            {/* Right Column */}
            <Box sx={styles.rightColumn}>
              {orderDetails && (
                <PatientCard
                  customer={orderDetails.customer as Orders.OrderCustomerDetails}
                  createdAt={orderDetails.createdAt}
                />
              )}

              <ListCard title="Vergangene Befundungen" count={previousBefundungen.length}>
                {previousBefundungen.length === 0 ? (
                  <Typography sx={styles.emptyText}>Keine Befundungen vorhanden</Typography>
                ) : (
                  previousBefundungen.map((order) => (
                    <Box key={order.requestId} sx={styles.historyItem}>
                      <Typography sx={styles.historyItemDate}>{formatDate(order.createdAt)}</Typography>
                      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                        <Typography sx={styles.historyItemTitle}>{order.product.productName}</Typography>
                        <Box sx={styles.checkBadge}>✓</Box>
                      </Box>
                    </Box>
                  ))
                )}
              </ListCard>

              <ListCard title="Bestellhistorie" count={orderHistoryWithoutCurrent.length}>
                {orderHistoryWithoutCurrent.length === 0 ? (
                  <Typography sx={styles.emptyText}>Keine Bestellungen vorhanden</Typography>
                ) : (
                  orderHistoryWithoutCurrent.map((order) => (
                    <Box key={order.requestId} sx={styles.orderItem}>
                      <Box sx={styles.orderItemLeft}>
                        <Typography sx={styles.orderItemTitle}>{order.product.productName}</Typography>
                        <Typography sx={styles.orderItemDate}>{formatDate(order.createdAt)}</Typography>
                      </Box>
                      <Box
                        sx={
                          order.orderState === Orders.OrderRequestState.ALLOWED
                            ? styles.checkBadge
                            : order.orderState === Orders.OrderRequestState.DENIED
                              ? styles.deniedBadge
                              : styles.countBadge
                        }
                      >
                        {order.orderState === Orders.OrderRequestState.ALLOWED
                          ? '✓'
                          : order.orderState === Orders.OrderRequestState.DENIED
                            ? '✗'
                            : '·'}
                      </Box>
                    </Box>
                  ))
                )}
              </ListCard>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};
